/* LINESHEET — Template 1 preview (default utilitarian template).
 * Sample brand: Wellness / Skincare. Cream + Sage. */
import { useState } from "react";
import { useCatalogFonts } from "./_fonts";
import {
  Search, ShoppingCart, Upload, Download, Filter, X,
  ChevronDown, ArrowLeft, Heart, Plus, Boxes, Package, Truck, Sparkles,
} from "lucide-react";

const SAMPLE_IMG_1 = "https://images.unsplash.com/photo-1739980213756-753aea153bb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxza2luY2FyZSUyMGJvdHRsZSUyMG5hdHVyYWx8ZW58MHx8fHwxNzgxODE4OTU1fDA&ixlib=rb-4.1.0&q=85";
const SAMPLE_IMG_2 = "https://images.unsplash.com/photo-1608571424266-edeb9bbefdec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxza2luY2FyZSUyMGJvdHRsZSUyMG5hdHVyYWx8ZW58MHx8fHwxNzgxODE4OTU1fDA&ixlib=rb-4.1.0&q=85";
const SAMPLE_IMG_3 = "https://images.unsplash.com/photo-1739980104488-408eff709fff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxza2luY2FyZSUyMGJvdHRsZSUyMG5hdHVyYWx8ZW58MHx8fHwxNzgxODE4OTU1fDA&ixlib=rb-4.1.0&q=85";

const PRODUCTS = [
  { sku: "WLN-001", name: "Lavender Calm Serum",      cat: "Serums",       price: 18.50, retail: 42, moq: 6,  stock: 240, img: SAMPLE_IMG_1, badge: "top_seller",     map: 36 },
  { sku: "WLN-002", name: "Rose Hydra Mist",          cat: "Toners",       price: 12.00, retail: 28, moq: 12, stock: 480, img: SAMPLE_IMG_2, badge: "new_arrival" },
  { sku: "WLN-003", name: "Glow Body Oil 100ml",      cat: "Body care",    price: 14.75, retail: 34, moq: 6,  stock: 120, img: SAMPLE_IMG_3, badge: null },
  { sku: "WLN-004", name: "Detox Kaolin Clay Mask",   cat: "Masks",        price: 9.20,  retail: 22, moq: 24, stock: 0,   img: SAMPLE_IMG_1, badge: "weekly_special", map: 19 },
  { sku: "WLN-005", name: "Rosehip Brightening Oil",  cat: "Serums",       price: 22.40, retail: 52, moq: 6,  stock: 88,  img: SAMPLE_IMG_2, badge: null },
  { sku: "WLN-006", name: "Aloe Cooling Gel 200ml",   cat: "Body care",    price: 8.90,  retail: 21, moq: 12, stock: 312, img: SAMPLE_IMG_3, badge: "top_seller" },
  { sku: "WLN-007", name: "Chamomile Eye Cream",      cat: "Eye care",     price: 16.30, retail: 39, moq: 6,  stock: 64,  img: SAMPLE_IMG_1, badge: null },
  { sku: "WLN-008", name: "Sea Salt Body Scrub",      cat: "Body care",    price: 11.60, retail: 26, moq: 12, stock: 200, img: SAMPLE_IMG_2, badge: "new_arrival" },
  { sku: "WLN-009", name: "Vitamin C Daily Drops",    cat: "Serums",       price: 24.80, retail: 56, moq: 6,  stock: 144, img: SAMPLE_IMG_3, badge: null,            map: 49 },
  { sku: "WLN-010", name: "Coconut Lip Balm Trio",    cat: "Lip care",     price: 7.40,  retail: 18, moq: 24, stock: 600, img: SAMPLE_IMG_1, badge: "todays_special" },
  { sku: "WLN-011", name: "Pure Argan Hair Oil",      cat: "Hair care",    price: 19.20, retail: 45, moq: 6,  stock: 36,  img: SAMPLE_IMG_2, badge: null },
  { sku: "WLN-012", name: "Restoring Night Balm",     cat: "Moisturizers", price: 26.50, retail: 62, moq: 6,  stock: 92,  img: SAMPLE_IMG_3, badge: "top_seller" },
];

const BADGE_META = {
  top_seller:     { label: "Top",     bg: "#FCE4B6", fg: "#5C3A05" },
  new_arrival:    { label: "New",     bg: "#D4E7D2", fg: "#234B25" },
  weekly_special: { label: "Weekly",  bg: "#F4CFCA", fg: "#6E2A22" },
  todays_special: { label: "Today",   bg: "#D1DCEC", fg: "#27396A" },
};

const COLORS = {
  bg:       "#F9F6F0",
  surface:  "#FFFFFF",
  primary:  "#849C8A",
  text:     "#1A2E22",
  muted:    "#5C7364",
  border:   "#E5DDD0",
};

export default function LinesheetPreview() {
  useCatalogFonts();
  const [cat, setCat] = useState("all");
  const [fulfill, setFulfill] = useState("all");
  const [quickBadge, setQuickBadge] = useState("all");

  return (
    <div className="min-h-screen" style={{ background: COLORS.bg, color: COLORS.text, fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="linesheet-preview">
      <PreviewRibbon name="Linesheet"/>

      {/* Sticky brand bar */}
      <div className="sticky top-9 z-20 border-b" style={{ background: COLORS.bg + "f5", borderColor: COLORS.border, backdropFilter: "blur(8px)" }}>
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full grid place-items-center text-base font-semibold" style={{ background: COLORS.primary, color: "#fff", fontFamily: "'Work Sans', sans-serif" }}>F</span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: COLORS.muted, fontFamily: "'JetBrains Mono', monospace" }}>Connected supplier</div>
              <div className="text-sm font-medium" style={{ fontFamily: "'Work Sans', sans-serif" }}>Flora Botanica · wholesale</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <BtnGhost icon={Upload}>Bulk upload</BtnGhost>
            <BtnGhost icon={Download}>Export</BtnGhost>
            <button className="h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2" style={{ background: COLORS.text, color: COLORS.bg }} data-testid="cart-btn">
              <ShoppingCart className="w-4 h-4"/>Cart <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: COLORS.primary, color: "#fff" }}>3</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Page heading */}
        <div className="mb-7">
          <div className="text-[11px] uppercase tracking-[0.22em]" style={{ color: COLORS.muted, fontFamily: "'JetBrains Mono', monospace" }}>Apps · Catalog</div>
          <h1 className="mt-2 tracking-tight" style={{ fontSize: 42, fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}>
            Shop catalog
          </h1>
          <div className="text-sm mt-1.5" style={{ color: COLORS.muted }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{PRODUCTS.length}</span> products · 1 verified supplier
          </div>
        </div>

        {/* Search row */}
        <div className="flex items-center gap-2.5 flex-wrap mb-5">
          <div className="relative flex-1 min-w-[300px] max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: COLORS.muted }}/>
            <input
              placeholder="Search products, brands, SKU, UPC…"
              className="w-full h-11 rounded-full pl-10 pr-28 text-sm focus:outline-none"
              style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text }}
            />
            <select className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 rounded-full px-3 text-xs border-0 cursor-pointer" style={{ background: COLORS.bg, color: COLORS.muted }}>
              <option>All fields</option><option>Name</option><option>Brand</option><option>SKU</option><option>UPC</option>
            </select>
          </div>
          <div className="flex gap-1.5 ml-auto flex-wrap">
            {[
              { k: "all", l: "All" },
              { k: "new_arrival", l: "New arrivals" },
              { k: "top_seller", l: "Top sellers" },
              { k: "weekly_special", l: "Weekly special" },
            ].map((b) => (
              <button
                key={b.k}
                onClick={() => setQuickBadge(b.k)}
                className="px-3.5 py-1.5 rounded-full text-xs transition-colors"
                style={{
                  background: quickBadge === b.k ? COLORS.text : COLORS.surface,
                  color: quickBadge === b.k ? COLORS.bg : COLORS.muted,
                  border: quickBadge === b.k ? "1px solid transparent" : `1px solid ${COLORS.border}`,
                }}
              >
                {b.l}
              </button>
            ))}
          </div>
          <button className="h-10 px-4 rounded-full text-xs inline-flex items-center gap-1.5" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.muted }}>
            Sort: Featured <ChevronDown className="w-3 h-3"/>
          </button>
        </div>

        {/* 2-col layout */}
        <div className="grid lg:grid-cols-[240px_1fr] gap-8">
          <aside className="space-y-8">
            <FilterGroup label="Categories" muted={COLORS.muted}>
              {["all", "Serums", "Toners", "Body care", "Masks", "Eye care", "Lip care", "Hair care", "Moisturizers"].map((k) => (
                <FilterRow key={k} active={cat === k} onClick={() => setCat(k)} primary={COLORS.text} bg={COLORS.bg}>
                  {k === "all" ? "All categories" : k}
                  <span className="text-[10px]" style={{ color: COLORS.muted }}>{k === "all" ? PRODUCTS.length : PRODUCTS.filter((p) => p.cat === k).length}</span>
                </FilterRow>
              ))}
            </FilterGroup>

            <FilterGroup label="Fulfillment" muted={COLORS.muted}>
              {[
                { k: "all", l: "All", icon: Boxes },
                { k: "wholesale", l: "Wholesale", icon: Package },
                { k: "dropship", l: "Dropship", icon: Truck },
                { k: "both", l: "Wholesale + Dropship", icon: Sparkles },
              ].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setFulfill(f.k)}
                  className="w-full text-left flex items-center gap-2.5 py-1.5 px-2 rounded-md text-xs transition-colors"
                  style={{
                    background: fulfill === f.k ? COLORS.text : "transparent",
                    color: fulfill === f.k ? COLORS.bg : COLORS.muted,
                  }}
                >
                  <f.icon className="w-3.5 h-3.5"/>{f.l}
                </button>
              ))}
            </FilterGroup>

            <FilterGroup label="Wholesale price" muted={COLORS.muted}>
              <input type="range" min={1} max={50} defaultValue={30} className="w-full" style={{ accentColor: COLORS.primary }}/>
              <div className="text-[11px] mt-1.5" style={{ color: COLORS.muted, fontFamily: "'JetBrains Mono', monospace" }}>Up to $30 per unit</div>
            </FilterGroup>

            <FilterGroup label="Min order qty (MOQ)" muted={COLORS.muted}>
              <input type="range" min={1} max={100} defaultValue={6} className="w-full" style={{ accentColor: COLORS.primary }}/>
              <div className="text-[11px] mt-1.5" style={{ color: COLORS.muted, fontFamily: "'JetBrains Mono', monospace" }}>6+ units</div>
            </FilterGroup>

            <button className="w-full text-xs inline-flex items-center justify-center gap-1 py-3 border-t" style={{ borderColor: COLORS.border, color: COLORS.muted }}>
              <X className="w-3 h-3"/>Clear filters
            </button>
          </aside>

          <main>
            <div className="flex items-center justify-between mb-4 text-xs" style={{ color: COLORS.muted }}>
              <div>Showing <span style={{ fontFamily: "'JetBrains Mono', monospace", color: COLORS.text, fontWeight: 600 }}>{PRODUCTS.length}</span> of {PRODUCTS.length} products</div>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {PRODUCTS.map((p) => <Card key={p.sku} p={p}/>)}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function Card({ p }) {
  const b = BADGE_META[p.badge];
  return (
    <div className="rounded-2xl overflow-hidden border" style={{ background: COLORS.surface, borderColor: COLORS.border }} data-testid={`prod-${p.sku}`}>
      <div className="aspect-square relative overflow-hidden" style={{ background: "#EFEAE0" }}>
        <img src={p.img} alt={p.name} className="w-full h-full object-cover" loading="lazy"/>
        {b && <span className="absolute top-2.5 left-2.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-medium" style={{ background: b.bg, color: b.fg, fontFamily: "'Work Sans', sans-serif" }}>{b.label}</span>}
        <span className="absolute top-2.5 right-2.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-medium" style={{ background: "#ffffffcc", color: COLORS.text, fontFamily: "'Work Sans', sans-serif" }}>{p.cat}</span>
        <button className="absolute bottom-2.5 right-2.5 w-8 h-8 rounded-full grid place-items-center" style={{ background: "#ffffffcc", color: COLORS.muted }}>
          <Heart className="w-3.5 h-3.5"/>
        </button>
      </div>
      <div className="p-4">
        <div className="text-[15px] leading-tight" style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}>{p.name}</div>
        <div className="text-[11px] mt-0.5" style={{ color: COLORS.muted, fontFamily: "'JetBrains Mono', monospace" }}>{p.sku}</div>

        <div className="mt-3 flex items-end justify-between">
          <div>
            <div className="text-[22px] tracking-tight leading-none" style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}>${p.price.toFixed(2)}</div>
            <div className="text-[11px] mt-1" style={{ color: COLORS.muted }}>MOQ {p.moq} · {p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}</div>
            {p.map && <div className="text-[10px] mt-0.5" style={{ color: "#B47A1F" }}>MAP ${p.map.toFixed(2)}</div>}
          </div>
          <div className="text-[10px]" style={{ color: COLORS.muted, textAlign: "right" }}>
            Retail<br/><span className="line-through">${p.retail}</span>
          </div>
        </div>

        <div className="mt-3.5 flex items-center gap-2">
          <div className="flex items-center rounded-full" style={{ border: `1px solid ${COLORS.border}` }}>
            <button className="w-7 h-7 grid place-items-center" style={{ color: COLORS.muted }}>−</button>
            <span className="w-8 text-center text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{p.moq}</span>
            <button className="w-7 h-7 grid place-items-center" style={{ color: COLORS.muted }}>+</button>
          </div>
          <button
            disabled={p.stock === 0}
            className="flex-1 h-9 rounded-full text-xs font-medium inline-flex items-center justify-center gap-1.5"
            style={{ background: p.stock === 0 ? COLORS.border : COLORS.text, color: p.stock === 0 ? COLORS.muted : COLORS.bg, fontFamily: "'Work Sans', sans-serif" }}
          >
            <Plus className="w-3 h-3"/>{p.stock === 0 ? "Notify me" : "Add to order"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, children, muted }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] mb-2.5" style={{ color: muted, fontFamily: "'JetBrains Mono', monospace" }}>{label}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function FilterRow({ active, onClick, children, primary, bg }) {
  return (
    <button onClick={onClick} className="w-full flex items-center justify-between text-left py-1.5 px-2 rounded-md text-xs transition-colors"
      style={{ background: active ? primary : "transparent", color: active ? bg : COLORS.muted }}>
      {children}
    </button>
  );
}

function BtnGhost({ icon: Icon, children }) {
  return (
    <button className="h-10 px-4 rounded-full text-xs inline-flex items-center gap-1.5 transition-colors" style={{ background: "transparent", border: `1px solid ${COLORS.border}`, color: COLORS.text }}>
      <Icon className="w-3.5 h-3.5"/>{children}
    </button>
  );
}

export function PreviewRibbon({ name }) {
  return (
    <div className="sticky top-0 z-30 h-9 px-4 flex items-center justify-between text-[11px]" style={{ background: "#0a1f1c", color: "#e8ff3a", fontFamily: "'JetBrains Mono', monospace" }}>
      <a href="/preview/catalog" className="inline-flex items-center gap-1.5 hover:opacity-80">
        <ArrowLeft className="w-3 h-3"/>Back to template gallery
      </a>
      <div className="opacity-70">Verqo · Catalog template preview · <strong className="text-white">{name}</strong></div>
      <div className="opacity-50">static mockup · no data is real</div>
    </div>
  );
}
